import { useCallback } from 'react';

import { toast } from 'sonner';

import { isFiniteNumber } from '@core';
import { useCreateActivity, useUpdateActivity } from '@queries';

type CreateActivityFlowOptions = {
    selectedClub: number;
};

const ERROR_MESSAGE = 'Failed to create activity';

/**
 * Creates a new activity with the selected club.
 *
 * Used when the user chooses to create a new activity instead of updating the current one.
 */
export const useCreateActivityFlow = ({ selectedClub }: CreateActivityFlowOptions) => {
    const { mutateAsync: createNewActivity, isPending: creating } = useCreateActivity({
        onError: (error) => {
            console.error('Failed to create activity', error);
            toast.error(ERROR_MESSAGE);
        },
    });
    const { mutateAsync: updateActivityClub, isPending: updating } = useUpdateActivity({
        onError: (error) => {
            console.error('Failed to update activity club', error);
            toast.error(ERROR_MESSAGE);
        },
    });

    const handleCreateWithClub = useCallback(async () => {
        const { id } = await createNewActivity(selectedClub);

        if (!isFiniteNumber(id)) {
            console.error(`CreateNewActivity failed to create a valid id expected a finite number got ${id}`);
            toast.error(ERROR_MESSAGE);
            return null;
        }

        return id;
    }, [createNewActivity, updateActivityClub, selectedClub]);

    return {
        handleCreateWithClub,
        isPending: creating || updating,
    };
};
